/**
 * Manual Asset Counts (AST-126)
 *
 * Totals manual asset counts by project across the selected customer: one
 * row per manually counted project or storage yard, one column per manual
 * asset, a Total column and a Grand Total row. Same look as Asset Counts
 * (MatLocationsDialog) -- header, gray group rows, pinned Location/Total,
 * Export, Close. The numbers come from utils/manualAssetCounts.
 *
 * Data: there is no customer-wide entries endpoint, so this reads each
 * manually counted project's entries through the same per-project call the
 * other screens use (any user of the customer may read them), plus the
 * customer's manual asset names once, through the project-scoped read
 * (also open to any user of the customer -- the customer-scoped one is
 * admin-only). A project that fails to load is reported, not silently
 * dropped, because a missing project would make the Grand Total wrong.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert, Box, Button, CircularProgress, IconButton, ListItemText, Menu, MenuItem, Paper,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography,
} from '@mui/material';
import { Close as CloseIcon, FileDownloadOutlined as DownloadIcon } from '@mui/icons-material';
import AppBar from '../elements/AppBar';
import PageBody from '../elements/PageBody';
import useProjectSelector from '../../hooks/useProjectSelector';
import { useAppSelector } from '../../store/hooks';
import { fetchEntries } from '../../services/countApi';
import { fetchProjectManualAssets } from '../../services/manualAssetsApi';
import { manualAssetCountsTable, isManualCounted } from '../../utils/manualAssetCounts';
import { toCsv, toXlsxSheet, exportFileName, downloadBlob } from '../../utils/assetCountsExport';

const TITLE = 'Manual Asset Counts';
const SHEET = 'Manual Asset Counts';
const LABELS = { rowLabel: 'Project', totalLabel: 'Grand Total' };

// Same look as Asset Counts (MatLocationsDialog).
const SURFACE = '#3A3938';
const NAME_W = 220;
const stickyLeft = { position: 'sticky', left: 0, zIndex: 2, bgcolor: SURFACE, minWidth: NAME_W, maxWidth: NAME_W };
const stickyRight = { position: 'sticky', right: 0, zIndex: 2, bgcolor: SURFACE, boxShadow: '-1px 0 0 rgba(255,255,255,0.12)' };
const GROUP_BG = '#5A5754';
const GROUP_TEXT = '#F2EEEC';
// MC-10: the Grand Total row stays visible when the table scrolls.
const stickyBottom = { position: 'sticky', bottom: 0, bgcolor: SURFACE, fontWeight: 700, borderTop: 2, borderColor: 'divider' };

const fmt = (n) => (n === 0 ? '0' : n.toLocaleString());
// MC-8: zeros gray, negatives red.
const cellColor = (n) => (n === 0 ? 'text.disabled' : n < 0 ? 'error.main' : 'text.primary');

/** Every page of a project's entries (same paging as countSlice.fetchEntries). */
async function allEntries(projectId) {
  const items = [];
  let nextToken;
  let pages = 0;
  do {
    // eslint-disable-next-line no-await-in-loop
    const r = await fetchEntries(projectId, { limit: 200, ...(nextToken ? { nextToken } : {}) });
    items.push(...(r.items || []));
    nextToken = r.nextToken;
    pages += 1;
  } while (nextToken && pages < 50);
  return items;
}

const SectionRow = ({ label, colSpan }) => (
  <TableRow>
    <TableCell colSpan={colSpan} component="th" scope="rowgroup" sx={{ bgcolor: GROUP_BG, color: GROUP_TEXT, fontWeight: 700, p: 0, textAlign: 'left' }}>
      <Box component="span" sx={{ position: 'sticky', left: 0, display: 'inline-block', px: 2, py: 0.75 }}>{label}</Box>
    </TableCell>
  </TableRow>
);

export default function ManualAssetCountsPage() {
  const navigate = useNavigate();
  const { ProjectSelectorComponent } = useProjectSelector();
  const projectList = useAppSelector((s) => s.project.projectList);
  const projectsLoaded = useAppSelector((s) => s.project.projectsLoaded);
  const customer = useAppSelector((s) => s.customer.activeCustomer);
  const exportedBy = useAppSelector((s) => s.auth?.userAttributes?.email || s.auth?.userAttributes?.name || '');

  const inScope = useMemo(() => (projectList || []).filter(isManualCounted), [projectList]);
  const scopeKey = inScope.map((p) => p.id).sort().join(',');

  const [state, setState] = useState({ loading: true, entriesById: {}, assets: [], failed: [] });

  const load = useCallback(async () => {
    if (!inScope.length) { setState({ loading: false, entriesById: {}, assets: [], failed: [] }); return; }
    setState((s) => ({ ...s, loading: true, failed: [] }));
    const results = await Promise.allSettled(inScope.map((p) => allEntries(p.id)));
    const entriesById = {};
    const failed = [];
    results.forEach((r, i) => {
      if (r.status === 'fulfilled') entriesById[inScope[i].id] = r.value;
      else failed.push(inScope[i].name);
    });
    let assets = [];
    try {
      // All in-scope projects share the customer, so one read covers them.
      assets = (await fetchProjectManualAssets(inScope[0].id))?.items || [];
    } catch {
      failed.push('manual asset names');
    }
    setState({ loading: false, entriesById, assets, failed });
  }, [scopeKey]); // eslint-disable-line react-hooks/exhaustive-deps -- scopeKey is inScope's identity

  // Reload when the nav drawer's customer (and so the project list) changes.
  useEffect(() => { if (projectsLoaded) load(); }, [projectsLoaded, load, customer?.id]);

  const table = useMemo(
    () => manualAssetCountsTable(inScope, state.entriesById, state.assets),
    [inScope, state.entriesById, state.assets]
  );
  const { columns } = table;
  const colSpan = columns.length + 2;

  // MC-13: back to wherever the user came from; Home if they landed here directly.
  const close = () => (window.history.length > 1 ? navigate(-1) : navigate('/'));

  // Export -- MC-12, same flow as Asset Counts.
  const exportAnchor = useRef(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState(false);
  const canExport = !state.loading && columns.length > 0;
  const runExport = async (kind) => {
    setMenuOpen(false);
    setExporting(true);
    setExportError(false);
    try {
      const now = new Date();
      const who = customer?.name || 'Customer';
      if (kind === 'csv') {
        downloadBlob(new Blob([toCsv(table, LABELS)], { type: 'text/csv;charset=utf-8' }), exportFileName(who, 'csv', now, TITLE));
      } else {
        const { default: writeXlsxFile } = await import('write-excel-file/browser');
        const { data, stickyRowsCount, columns: widths } = toXlsxSheet(table, { projectName: who, user: exportedBy, now, labels: LABELS });
        const blob = await writeXlsxFile(data, { sheet: SHEET, stickyRowsCount, columns: widths }).toBlob();
        downloadBlob(blob, exportFileName(who, 'xlsx', now, TITLE));
      }
    } catch (err) {
      console.error('[AST-126] Manual Asset Counts export failed', err);
      setExportError(true);
    } finally {
      setExporting(false);
    }
  };

  const dataRow = (r) => (
    <TableRow key={r.id} hover>
      <TableCell component="th" scope="row" sx={stickyLeft}>{r.name}</TableCell>
      {r.cells.map((n, i) => (
        <TableCell key={columns[i].id} align="right" sx={{ color: cellColor(n), whiteSpace: 'nowrap' }}>{fmt(n)}</TableCell>
      ))}
      <TableCell align="right" sx={{ ...stickyRight, fontWeight: 700, color: r.total < 0 ? 'error.main' : 'text.primary' }}>{fmt(r.total)}</TableCell>
    </TableRow>
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', bgcolor: 'background.default' }}>
      <AppBar showProjectSelector projectSelectorProps={{ ProjectSelectorComponent }} />
      <PageBody>
        <Paper variant="outlined" sx={{ display: 'flex', flexDirection: 'column', bgcolor: SURFACE }}>
          {/* MC-4: same header layout as Asset Counts */}
          <Box sx={{ position: 'relative', px: 3, pt: 2.5, pb: 2, pr: 7 }}>
            <Typography component="h1" variant="h6">{TITLE}</Typography>
            <Typography variant="body2" color="text.secondary">Manually counted assets by project</Typography>
            <IconButton aria-label="Close manual asset counts" onClick={close} sx={{ position: 'absolute', right: 8, top: 8 }}>
              <CloseIcon />
            </IconButton>
          </Box>

          <Box sx={{ borderTop: 1, borderBottom: 1, borderColor: 'divider' }}>
            {state.failed.length > 0 && (
              <Alert severity="warning" sx={{ m: 2 }} action={<Button color="inherit" size="small" onClick={load}>Retry</Button>}>
                {`Couldn't load ${state.failed.join(', ')}. The totals below leave ${state.failed.length === 1 ? 'it' : 'them'} out.`}
              </Alert>
            )}
            {state.loading || !projectsLoaded ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress aria-label="Loading manual asset counts" /></Box>
            ) : columns.length === 0 ? (
              <Typography sx={{ p: 3 }} color="text.secondary">No manual assets have been counted on this customer&apos;s projects yet.</Typography>
            ) : (
              <TableContainer sx={{ maxHeight: '65vh', overflow: 'auto' }}>
                <Table stickyHeader size="small" aria-label="Manually counted assets by project">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ ...stickyLeft, zIndex: 3, fontWeight: 700 }}>Project</TableCell>
                      {columns.map((c) => (
                        <TableCell key={c.id} align="right" sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>{c.name}</TableCell>
                      ))}
                      <TableCell align="right" sx={{ ...stickyRight, zIndex: 3, fontWeight: 700 }}>Total</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {table.groups.map((g) => (
                      <React.Fragment key={g.key}>
                        <SectionRow label={g.label} colSpan={colSpan} />
                        {g.rows.map(dataRow)}
                      </React.Fragment>
                    ))}
                    <TableRow data-testid="grand-total-row">
                      <TableCell component="th" scope="row" sx={{ ...stickyBottom, ...stickyLeft, zIndex: 3, fontWeight: 700 }}>Grand Total</TableCell>
                      {table.grandTotal.cells.map((n, i) => (
                        <TableCell key={columns[i].id} align="right" sx={{ ...stickyBottom, zIndex: 1, color: cellColor(n), whiteSpace: 'nowrap' }}>{fmt(n)}</TableCell>
                      ))}
                      <TableCell align="right" sx={{ ...stickyBottom, ...stickyRight, zIndex: 3, fontWeight: 700 }}>{fmt(table.grandTotal.total)}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1, px: 2, py: 1.5 }}>
            {exportError && (
              <Typography role="alert" variant="body2" color="error" sx={{ mr: 'auto', pl: 1 }}>Export failed. Try again.</Typography>
            )}
            {canExport && (
              <>
                <Button
                  ref={exportAnchor}
                  variant="outlined"
                  startIcon={exporting ? <CircularProgress size={16} aria-label="Exporting" /> : <DownloadIcon />}
                  onClick={() => setMenuOpen(true)}
                  disabled={exporting}
                  aria-haspopup="menu"
                  sx={{ textTransform: 'none' }}
                >
                  Export
                </Button>
                <Menu
                  anchorEl={exportAnchor.current}
                  open={menuOpen}
                  onClose={() => setMenuOpen(false)}
                  anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
                  transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                >
                  <MenuItem onClick={() => runExport('csv')}><ListItemText>CSV (.csv)</ListItemText></MenuItem>
                  <MenuItem onClick={() => runExport('xlsx')}><ListItemText>Excel (.xlsx)</ListItemText></MenuItem>
                </Menu>
              </>
            )}
            <Button onClick={close} sx={{ textTransform: 'none' }}>Close</Button>
          </Box>
        </Paper>
      </PageBody>
    </Box>
  );
}
